import { mapColumns, parseCsv } from './csv';
import { registrationFromCallsign } from './identity';
import type { FormatParser, ParsedPoint, ParsedTrack } from './types';
import { ParseError } from './types';
import { parseLooseNumber } from './units';

/**
 * Flightradar24 CSV, and any CSV close enough to read the same way.
 *
 * A real export from a Silver account looks like this:
 *
 *     Timestamp,UTC,Callsign,Position,Altitude,Speed,Direction
 *     1789507694,2026-09-15T21:28:14Z,ZKDFH,"-37.923626,176.918381",0,0,8
 *
 * Column *indices* are never assumed. `DATA-SOURCES.md` §1 is explicit about
 * why: FR24 has changed these headers before, other vendors emit the same data
 * under different names, and a shifted column produces numbers that look right.
 * So each field is matched by name against a synonym list, and a file with no
 * recognisable position column fails loudly with the headers it actually found.
 */

type Field =
  | 'timestamp'
  | 'utc'
  | 'callsign'
  | 'position'
  | 'lat'
  | 'lon'
  | 'altitude'
  | 'speed'
  | 'direction';

const SYNONYMS: Readonly<Record<Field, readonly string[]>> = {
  timestamp: ['timestamp', 'time stamp', 'epoch', 'unix', 'unix time'],
  utc: ['utc', 'time', 'date time', 'datetime', 'timestamp utc', 'time utc'],
  callsign: ['callsign', 'call sign', 'flight', 'registration', 'reg', 'tail', 'aircraft'],
  // FR24 puts both halves in one quoted field; other exports split them.
  position: ['position', 'coordinates', 'coords', 'latlon', 'lat lon'],
  lat: ['latitude', 'lat'],
  lon: ['longitude', 'lon', 'lng', 'long'],
  altitude: ['altitude', 'alt', 'altitude ft', 'alt ft', 'baro altitude', 'gps altitude'],
  speed: ['speed', 'groundspeed', 'ground speed', 'gs', 'speed kts', 'velocity'],
  direction: ['direction', 'heading', 'track', 'course', 'bearing'],
};

/**
 * Parse a `"lat,lon"` cell.
 *
 * @returns `[lat, lon]`, or `[NaN, NaN]` when the cell is not a pair.
 */
function parsePositionCell(cell: string): [number, number] {
  const parts = cell.split(/[,;\s]+/).filter((part) => part !== '');
  if (parts.length < 2) {
    return [Number.NaN, Number.NaN];
  }
  return [parseLooseNumber(parts[0]), parseLooseNumber(parts[1])];
}

/**
 * Read the time for a row, preferring the epoch column.
 *
 * The epoch is unambiguous. The `UTC` column is a formatted string and FR24
 * has emitted it as both `2026-09-15T21:28:14Z` and `2026-09-15 21:28:14`; the
 * second is parsed as *local* time by `Date`, which silently shifts a whole
 * flight by the machine's offset. So when falling back to it, the `Z` is added
 * if it is missing rather than trusting the host's timezone.
 */
function parseRowTime(epochCell: string | undefined, utcCell: string | undefined): number {
  const epoch = parseLooseNumber(epochCell);
  if (Number.isFinite(epoch) && epoch > 0) {
    // Seconds if it looks like seconds, milliseconds if it looks like those.
    return epoch > 1e11 ? epoch : epoch * 1000;
  }
  const raw = utcCell?.trim();
  if (raw === undefined || raw === '') {
    return Number.NaN;
  }
  const iso = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(raw)
    ? raw.replace(' ', 'T')
    : `${raw.replace(' ', 'T')}Z`;
  const parsed = Date.parse(iso);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
}

function parseFr24Csv(bytes: Uint8Array): ParsedTrack {
  const rows = parseCsv(new TextDecoder().decode(bytes));
  const headerRow = rows[0];
  if (headerRow === undefined) {
    throw new ParseError('This CSV file is empty.');
  }

  const columns = mapColumns(headerRow, SYNONYMS);
  const hasPosition = columns.position !== -1;
  const hasSplitLatLon = columns.lat !== -1 && columns.lon !== -1;

  if (!hasPosition && !hasSplitLatLon) {
    throw new ParseError(
      'No position column recognised in this CSV. Expected a "Position" column, or separate "Latitude" and "Longitude" columns.',
      [`Columns found: ${headerRow.join(', ')}`],
    );
  }
  if (columns.timestamp === -1 && columns.utc === -1) {
    throw new ParseError(
      'No time column recognised in this CSV. Expected a "Timestamp" or "UTC" column.',
      [`Columns found: ${headerRow.join(', ')}`],
    );
  }

  const points: ParsedPoint[] = [];
  const callsigns = new Set<string>();

  for (let r = 1; r < rows.length; r += 1) {
    const row = rows[r];
    if (row === undefined || row.length === 0) {
      continue;
    }

    let lat: number;
    let lon: number;
    if (hasPosition) {
      [lat, lon] = parsePositionCell(row[columns.position] ?? '');
    } else {
      lat = parseLooseNumber(row[columns.lat]);
      lon = parseLooseNumber(row[columns.lon]);
    }

    points.push({
      tMs: parseRowTime(row[columns.timestamp], row[columns.utc]),
      lat,
      lon,
      altFt: columns.altitude === -1 ? Number.NaN : parseLooseNumber(row[columns.altitude]),
      gsKt: columns.speed === -1 ? Number.NaN : parseLooseNumber(row[columns.speed]),
      trackDeg: columns.direction === -1 ? Number.NaN : parseLooseNumber(row[columns.direction]),
    });

    const callsign = row[columns.callsign]?.trim();
    if (callsign !== undefined && callsign !== '') {
      callsigns.add(callsign);
    }
  }

  // One callsign for the file is the normal case; a ferry flight re-filed
  // mid-air would give two, and picking one silently would be a guess.
  const callsign = callsigns.size === 1 ? [...callsigns][0] : undefined;
  const notes = [
    `Columns read: ${(Object.keys(SYNONYMS) as Field[])
      .filter((field) => columns[field] !== -1)
      .map((field) => headerRow[columns[field]] ?? field)
      .join(', ')}`,
  ];
  if (callsigns.size > 1) {
    notes.push(`Several callsigns in one file: ${[...callsigns].join(', ')}`);
  }

  const registration = registrationFromCallsign(callsign);

  return {
    source: 'fr24-csv',
    points,
    /*
     * ADS-B altitude is pressure altitude referenced to 1013.25 hPa, which is
     * not what the altimeter read unless the QNH happened to be standard.
     * Labelling it `baro` is what stops the map calling it a GPS height.
     */
    altitudeRef: 'baro',
    ...(callsign !== undefined ? { callsign } : {}),
    ...(registration !== undefined ? { registration } : {}),
    warnings: [],
    notes,
  };
}

export const fr24CsvParser: FormatParser = {
  format: 'fr24-csv',
  label: 'Flightradar24 CSV',
  extensions: ['.csv'],
  sniff(text) {
    const firstLine = text.slice(0, 2000).split(/\r?\n/)[0] ?? '';
    if (!firstLine.includes(',')) {
      return false;
    }
    const columns = mapColumns(firstLine.split(','), SYNONYMS);
    const hasPosition = columns.position !== -1 || (columns.lat !== -1 && columns.lon !== -1);
    return hasPosition && (columns.timestamp !== -1 || columns.utc !== -1);
  },
  parse: parseFr24Csv,
};
